import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { CreateCollaboratorModal } from "@/components/collaborators/CreateCollaboratorModal";
import { UpdateCollaboratorModal } from "@/components/collaborators/UpdateCollaboratorModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Loader2,
  Search,
  UserCog,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  collaboratorService,
  ECollaboratorStatus,
  ECollaboratorRole,
  Collaborator,
} from "@/services/collaborator";
import { useUrlFilters, useUrlPage, useUrlSearch } from "@/hooks/use-url-filters";

function generatePaginationNumbers(
  currentPage: number,
  totalPages: number,
): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== "...") {
      range.push("...");
    }
  }

  return range;
}

const ROLE_LABELS: Record<ECollaboratorRole, string> = {
  [ECollaboratorRole.OWNER]: "Propietario",
  [ECollaboratorRole.SELLER]: "Vendedor",
};

const PAGE_SIZE = 10;

const DEFAULT_FILTERS = {
  status: ECollaboratorStatus.ACTIVE as string,
  role: "all",
};

export default function Collaborators() {
  const [searchQuery, setSearchQuery, debouncedSearch] = useUrlSearch("search");
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const statusFilter = filters.status;
  const roleFilter = filters.role;
  const [currentPage, setCurrentPage] = useUrlPage();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] =
    useState<Collaborator | null>(null);

  const {
    data: collaboratorsResponse,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "collaborators",
      currentPage,
      debouncedSearch,
      statusFilter,
      roleFilter,
    ],
    queryFn: () =>
      collaboratorService.getAll({
        page: currentPage,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status:
          statusFilter !== "all"
            ? (statusFilter as ECollaboratorStatus)
            : undefined,
        role:
          roleFilter !== "all" ? (roleFilter as ECollaboratorRole) : undefined,
      }),
  });

  const collaborators = collaboratorsResponse?.data || [];
  const pagination = collaboratorsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  const handleEdit = (collaborator: Collaborator) => {
    setEditingCollaborator(collaborator);
    setIsUpdateModalOpen(true);
  };

  return (
    <DashboardLayout
      title="Colaboradores"
      subtitle="Gestiona los colaboradores con acceso al sistema"
    >
      <div className="space-y-6 animate-fade-in">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar por nombre, usuario o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-72"
              />
            </div>

            <Select value={statusFilter} onValueChange={(value) => setFilters({ status: value })}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value={ECollaboratorStatus.ACTIVE}>
                  Activo
                </SelectItem>
                <SelectItem value={ECollaboratorStatus.INACTIVE}>
                  Inactivo
                </SelectItem>
                <SelectItem value={ECollaboratorStatus.SUSPENDED}>
                  Suspendido
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={roleFilter} onValueChange={(value) => setFilters({ role: value })}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value={ECollaboratorRole.OWNER}>
                  Propietario
                </SelectItem>
                <SelectItem value={ECollaboratorRole.SELLER}>
                  Vendedor
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button className="gap-2" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Agregar colaborador
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Table */}
        {!isLoading && (
          <div className="card-elevated overflow-hidden relative">
            {isFetching && !isLoading && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10 rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha de registro</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collaborators.map((collaborator) => (
                  <TableRow key={collaborator.id} className="hover:bg-muted/30">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <UserCog className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium text-foreground">
                          {collaborator.firstName} {collaborator.lastName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {collaborator.userName}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {collaborator.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ROLE_LABELS[collaborator.role]}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={collaborator.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(collaborator.createdAt).toLocaleDateString(
                        "es-CO",
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(collaborator)}
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar colaborador</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {collaborators.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">
                  No se encontraron colaboradores
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Ajusta los filtros o agrega un nuevo colaborador
                </p>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {collaborators.length} de {totalItems} colaboradores
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map(
                  (page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    ) : (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        className="w-9"
                        onClick={() => setCurrentPage(page as number)}
                        disabled={isFetching}
                      >
                        {page}
                      </Button>
                    ),
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <CreateCollaboratorModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
      {editingCollaborator && (
        <UpdateCollaboratorModal
          open={isUpdateModalOpen}
          onOpenChange={setIsUpdateModalOpen}
          collaborator={editingCollaborator}
        />
      )}
    </DashboardLayout>
  );
}
